import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Header from '../Components/Header';

const LoginContainer = styled.div`
  min-height: 100vh;
  background-color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
  }
`;

const LoginContent = styled.div`
  padding: 40px 24px 24px 24px;
  max-width: 400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 40px 20px 24px 20px;
  }
`;

const LoginTitle = styled.h1`
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
  text-align: left;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const LoginSubtitle = styled.p`
  font-size: 18px;
  color: #666;
  margin-bottom: 32px;
  text-align: left;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InputLabel = styled.label`
  font-size: 18px;
  color: #333;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const InputField = styled.input`
  padding: 18px;
  border: 2px solid #e1e5e9;
  border-radius: 12px;
  font-size: 18px;
  color: #333;
  background-color: white;
  transition: border-color 0.3s ease;
  font-family: 'Noto Sans Thai', sans-serif !important;
  font-weight: bold;

  &:focus {
    outline: none;
    border-color: #00BBD3;
  }

  &::placeholder {
    color: #999;
    font-weight: bold;
    font-family: 'Noto Sans Thai', sans-serif !important;
  }
`;

const DateSelectContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const DateButton = styled.div`
  flex: 1;
  padding: 18px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 12px;
  font-size: 18px;
  color: ${props => props.hasValue ? '#333' : '#999'};
  background-color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
  font-weight: bold;
  cursor: pointer;
  transition: border-color 0.3s ease;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    border-color: #00BBD3;
  }

  &.focused {
    border-color: #00BBD3;
  }
`;

const DropdownIcon = styled.div`
  width: 16px;
  height: 16px;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2300BBD3' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 16px;
`;

const DatePickerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${props => props.show ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const DatePickerModal = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin: 20px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-height: 80vh;
  overflow-y: auto;
`;

const PickerHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const PickerTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin: 0 0 8px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const PickerSubtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const PickerGrid = styled.div`
  display: grid;
  gap: 8px;
  margin-bottom: 20px;
  
  /* Day: 7 columns for weeks */
  ${props => props.type === 'day' && `
    grid-template-columns: repeat(7, 1fr);
  `}
  
  /* Month: 3 columns, 4 rows */
  ${props => props.type === 'month' && `
    grid-template-columns: repeat(3, 1fr);
  `}
  
  /* Year: 4 columns */
  ${props => props.type === 'year' && `
    grid-template-columns: repeat(4, 1fr);
    max-height: 300px;
    overflow-y: auto;
  `}
`;

const PickerItem = styled.div`
  padding: 12px 8px;
  text-align: center;
  font-size: 16px;
  cursor: pointer;
  border-radius: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
  font-weight: bold;
  opacity: ${props => props.disabled ? 0.3 : 1};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  
  ${props => props.isSelected ? `
    background-color: #00BBD3 !important;
    color: white !important;
  ` : `
    background-color: transparent;
    color: #333;
  `}
  
  &:hover {
    ${props => {
      if (props.disabled) return `
        background-color: transparent;
        color: #333;
      `;
      if (props.isSelected) return `
        background-color: #00BBD3 !important;
        color: white !important;
      `;
      return `
        background-color: #f0f9ff;
        color: #333;
      `;
    }}
  }

  &:active {
    ${props => props.disabled ? `
      background-color: transparent;
      color: #333;
    ` : `
      background-color: #00BBD3 !important;
      color: white !important;
    `}
  }

  ${props => props.type === 'year' && `
    font-size: 14px;
    padding: 10px 6px;
  `}
`;

const PickerActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 12px;
  border: 2px solid #00BBD3;
  border-radius: 30px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Noto Sans Thai', sans-serif !important;
  box-shadow: 0 9px 14px 0 rgba(0, 187, 211, 0.08);
  
  ${props => props.primary ? `
    background-color: #00BBD3;
    color: white;
    
    &:hover {
      background-color: #009bb3;
    }
  ` : `
    background-color: white;
    color: #00BBD3;
    
    &:hover {
      background-color: #f0f9ff;
    }
  `}
`;

const DateDropdowns = ({ value, onChange }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [pickerType, setPickerType] = useState('day'); // 'day', 'month', 'year'
  const [tempDate, setTempDate] = useState({ day: '', month: '', year: '' });

  // Parse existing value or set defaults
  const parseDate = (dateString) => {
    if (!dateString) return { day: '', month: '', year: '' };
    const date = new Date(dateString);
    return {
      day: date.getDate().toString(),
      month: (date.getMonth() + 1).toString(),
      year: date.getFullYear().toString()
    };
  };

  const [dateValues, setDateValues] = useState(parseDate(value));

  // Update local state when value prop changes
  React.useEffect(() => {
    setDateValues(parseDate(value));
  }, [value]);

  const monthNames = [
    'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
  ];

  const getCurrentYear = () => new Date().getFullYear();
  const getYearOptions = () => {
    const currentYear = getCurrentYear();
    const years = [];
    for (let year = currentYear - 80; year <= currentYear; year++) {
      years.push(year);
    }
    return years.reverse();
  };

  const getDaysInMonth = (month, year) => {
    if (!month || !year) return 31;
    return new Date(year, month, 0).getDate();
  };

  const formatDisplayDate = () => {
    if (!dateValues.day && !dateValues.month && !dateValues.year) return 'เลือกวันเดือนปีเกิด';
    if (!dateValues.day || !dateValues.month || !dateValues.year) return 'เลือกวันเดือนปีเกิด';
    
    const day = String(dateValues.day).padStart(2, '0');
    const month = String(dateValues.month).padStart(2, '0');
    const year = parseInt(dateValues.year) + 543;
    return `${day}/${month}/${year}`;
  };

  const handleOpen = (type) => {
    setPickerType(type);
    setTempDate({ ...dateValues });
    setShowPicker(true);
  };

  const handleSelect = (type, value) => {
    setTempDate(prev => {
      const newTempDate = { ...prev, [type]: value };
      console.log('Selected:', type, value, 'New tempDate:', newTempDate);
      return newTempDate;
    });
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDateValues(tempDate);
    
    if (tempDate.day && tempDate.month && tempDate.year) {
      const dateString = `${tempDate.year}-${String(tempDate.month).padStart(2, '0')}-${String(tempDate.day).padStart(2, '0')}`;
      onChange(dateString);
    }
    
    setShowPicker(false);
  };

  const handleCancel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setTempDate({ ...dateValues });
    setShowPicker(false);
  };

  const renderDayGrid = () => {
    const maxDays = getDaysInMonth(tempDate.month, tempDate.year);
    const days = Array.from({ length: maxDays }, (_, i) => i + 1);
    
    return (
      <PickerGrid type="day">
        {days.map((day) => (
          <PickerItem
            key={`day-${day}-${tempDate.day}`}
            isSelected={tempDate.day === day.toString()}
            onClick={() => handleSelect('day', day.toString())}
            disabled={!tempDate.month || !tempDate.year}
          >
            {day}
          </PickerItem>
        ))}
      </PickerGrid>
    );
  };

  const renderMonthGrid = () => (
    <PickerGrid type="month">
      {monthNames.map((month, index) => (
        <PickerItem
          key={`month-${index}-${tempDate.month}`}
          isSelected={tempDate.month === (index + 1).toString()}
          onClick={() => handleSelect('month', (index + 1).toString())}
        >
          {month}
        </PickerItem>
      ))}
    </PickerGrid>
  );

  const renderYearGrid = () => (
    <PickerGrid type="year">
      {getYearOptions().map((year) => (
        <PickerItem
          key={`year-${year}-${tempDate.year}`}
          type="year"
          isSelected={tempDate.year === year.toString()}
          onClick={() => handleSelect('year', year.toString())}
        >
          {year + 543}
        </PickerItem>
      ))}
    </PickerGrid>
  );

  return (
    <>
      <DateSelectContainer>
        <DateButton
          hasValue={!!dateValues.day}
          className={showPicker && pickerType === 'day' ? 'focused' : ''}
          onClick={() => handleOpen('day')}
        >
          <span>{dateValues.day || 'วัน'}</span>
          <DropdownIcon />
        </DateButton>

        <DateButton
          hasValue={!!dateValues.month}
          className={showPicker && pickerType === 'month' ? 'focused' : ''}
          onClick={() => handleOpen('month')}
        >
          <span>{dateValues.month ? monthNames[parseInt(dateValues.month) - 1] : 'เดือน'}</span>
          <DropdownIcon />
        </DateButton>

        <DateButton
          hasValue={!!dateValues.year}
          className={showPicker && pickerType === 'year' ? 'focused' : ''}
          onClick={() => handleOpen('year')}
        >
          <span>{dateValues.year ? (parseInt(dateValues.year) + 543) : 'ปี'}</span>
          <DropdownIcon />
        </DateButton>
      </DateSelectContainer>

      <DatePickerOverlay show={showPicker} onClick={handleCancel}>
        <DatePickerModal onClick={(e) => e.stopPropagation()}>
          <PickerHeader>
            <PickerTitle>
              {pickerType === 'day' && 'เลือกวัน'}
              {pickerType === 'month' && 'เลือกเดือน'}
              {pickerType === 'year' && 'เลือกปี'}
            </PickerTitle>
            <PickerSubtitle>
              {formatDisplayDate()}
            </PickerSubtitle>
          </PickerHeader>

          {pickerType === 'day' && renderDayGrid()}
          {pickerType === 'month' && renderMonthGrid()}
          {pickerType === 'year' && renderYearGrid()}

          <PickerActions>
            <ActionButton type="button" onClick={handleCancel}>ยกเลิก</ActionButton>
            <ActionButton type="button" primary onClick={handleConfirm}>ตกลง</ActionButton>
          </PickerActions>
        </DatePickerModal>
      </DatePickerOverlay>
    </>
  );
};

const CheckboxGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 8px;
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 20px;
  height: 20px;
  border: 2px solid #00BBD3;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  flex-shrink: 0;
  margin-top: 2px;
  appearance: none;
  
  &:checked {
    background-color: #00BBD3;
    background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='m13.854 3.646-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 9.293l6.646-6.647a.5.5 0 0 1 .708.708z'/%3e%3c/svg%3e");
    background-size: 12px;
    background-position: center;
    background-repeat: no-repeat;
  }
`;

const CheckboxLabel = styled.label`
  font-size: 16px;
  color: #666;
  line-height: 1.4;
  cursor: pointer;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const PrivacyLink = styled.span`
  color: #00BBD3;
  text-decoration: underline;
  cursor: pointer;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  
  &:hover {
    color: #009bb3;
  }
`;

const LoginButton = styled.button`
  background-color: #00BBD3;
  color: white;
  border: none;
  border-radius: 30px;
  padding: 18px 32px;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 16px;
  width: 100%;
  font-family: 'Noto Sans Thai', sans-serif !important;
  box-shadow: 0 9px 14px 0 rgba(0, 187, 211, 0.08);
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover {
    background-color: ${props => props.disabled ? '#00BBD3' : '#009bb3'};
  }

  &:active {
    background-color: ${props => props.disabled ? '#00BBD3' : '#008aa0'};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const CreateAccountSection = styled.div`
  margin-top: 40px;
  text-align: center;
`;

const CreateAccountText = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 16px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const CreateAccountButton = styled.button`
  background-color: white;
  color: #00BBD3;
  border: 2px solid #00BBD3;
  border-radius: 30px;
  padding: 18px 32px;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  font-family: 'Noto Sans Thai', sans-serif !important;
  box-shadow: 0 9px 14px 0 rgba(0, 187, 211, 0.08);

  &:hover {
    background-color: #00BBD3;
    color: white;
  }

  &:active {
    background-color: #009bb3;
  }
`;

const ErrorNote = styled.div`
  color: #ff4757;
  font-size: 14px;
  margin-top: 8px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  display: ${props => props.show ? 'block' : 'none'};
`;

const Login = () => {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [isAccepted, setIsAccepted] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [birthError, setBirthError] = useState(false);

  // Mock data
  const MOCK_PHONE = '1929900891511';
  const MOCK_BIRTH_DATE = '2003-04-15'; // ปี 2546 = 2003

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!isAccepted) {
      alert('กรุณาอ่านและยอมรับ ประกาศนโยบายความเป็นส่วนตัว');
      return;
    }

    // Reset errors first
    setPhoneError(false);
    setBirthError(false);

    let hasError = false;

    // Validate phone number
    if (phoneNumber !== MOCK_PHONE) {
      setPhoneError(true);
      hasError = true;
    }

    // Validate birth date
    if (birthDate !== MOCK_BIRTH_DATE) {
      setBirthError(true);
      hasError = true;
    }

    // Only navigate if no errors
    if (!hasError) {
      navigate('/otp');
    }
  };

  const handleCreateAccount = () => {
    // ไปหน้าสมัครสมาชิก
    navigate('/register');
  };

  const handlePrivacyClick = () => {
    window.open('https://dietz.asia/terms.html', '_blank');
  };

  return (
    <LoginContainer>
      <Header />
      
      <LoginContent>
        <LoginTitle>เข้าสู่ระบบ</LoginTitle>
        <LoginSubtitle>กรุณากรอกเลขบัตรประชาชน</LoginSubtitle>
        
        <LoginForm onSubmit={handleSubmit}>
          <InputGroup>
            <InputField
              type="tel"
              placeholder="เลข 13 หลัก"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              maxLength="13"
            />
            <ErrorNote show={phoneError}>
              เลขบัตรประชาชนไม่มีในระบบ กรุณาลองอีกครั้ง
            </ErrorNote>
          </InputGroup>
          
          <InputGroup>
            <InputLabel>วันเดือนปีเกิด</InputLabel>
            <DateDropdowns
              value={birthDate}
              onChange={(date) => setBirthDate(date)}
            />
            <ErrorNote show={birthError}>
              วันเดือนปีเกิดไม่ถูกต้อง กรุณาลองอีกครั้ง
            </ErrorNote>
          </InputGroup>
          
          <CheckboxGroup>
            <Checkbox
              id="privacy-policy"
              checked={isAccepted}
              onChange={(e) => setIsAccepted(e.target.checked)}
            />
            <CheckboxLabel htmlFor="privacy-policy">
              ฉันได้อ่านและยอมรับ ประกาศนโยบายความเป็นส่วนตัว{' '}
              <PrivacyLink onClick={handlePrivacyClick}>(Privacy Notice)</PrivacyLink>
            </CheckboxLabel>
          </CheckboxGroup>
          
          <LoginButton type="submit">
            เข้าสู่ระบบ
          </LoginButton>
        </LoginForm>
        
        <CreateAccountSection>
          <CreateAccountText>หากคุณยังไม่ได้ลงทะเบียน</CreateAccountText>
          <CreateAccountButton 
            type="button"
            onClick={handleCreateAccount}
          >
            คลิกที่นี่
          </CreateAccountButton>
        </CreateAccountSection>
      </LoginContent>
    </LoginContainer>
  );
};

export default Login;